import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, FileText } from "lucide-react";
import { generateReceiptPdf } from "@/lib/receipt";

const FIELDS = [
  ["hostel_name", "Hostel Name"],
  ["receipt_prefix", "Receipt Prefix (e.g. VBH)"],
  ["gst_pan", "GST / PAN"],
  ["address_line_1", "Address Line 1"],
  ["address_line_2", "Address Line 2"],
  ["header_note", "Header note / tagline"],
  ["signatory_name", "Signatory Name"],
  ["signatory_role", "Signatory Role"],
  ["terms_line", "Terms line"],
  ["footer_note", "Footer note"],
];

export default function ReceiptTemplateTab() {
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const load = async () => {
    const { data } = await api.get("/receipt-template");
    setTpl(data || {});
    setLastSavedAt(data?.updated_at || data?.created_at || null);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!tpl) {
      toast.error("Template still loading — please wait");
      return;
    }
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        FIELDS.map(([k]) => [k, tpl[k] ?? ""]),
      );
      const { data } = await api.put("/receipt-template", payload);
      setTpl(data || {});
      setLastSavedAt(data?.updated_at || new Date().toISOString());
      toast.success("Template saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const preview = () => {
    if (!tpl) {
      toast.error("Template still loading");
      return;
    }
    generateReceiptPdf({
      receipt_number: `${tpl.receipt_prefix || "VBH"}/${new Date().getFullYear()}/0001`,
      student_name: "Sample Student",
      student_id_number: "PGABC123",
      flat_number: "204",
      kl_id: "KL2024001",
      amount: 15000,
      payment_method: "Cash",
      payment_date: new Date().toISOString(),
      total_fees: 60000,
      discount_amount: 3000,
      fees_paid_after: 30000,
      balance_due: 27000,
      notes: "October part-payment received. Preview receipt.",
      issued_by: "Admin",
      template_snapshot: tpl,
    });
  };

  return (
    <div className="space-y-6" data-testid="receipt-template-tab">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h3 className="font-heading font-black text-3xl tracking-tighter">
            Fee Receipt Template
          </h3>
          <p className="text-sm font-medium text-[#4A4A4A] mt-1">
            Every receipt is a snapshot of this template at the moment of issue — older receipts stay unchanged when you tweak this.
          </p>
          {lastSavedAt && (
            <p
              className="text-xs font-bold text-[#00CC66] mt-1"
              data-testid="template-last-saved"
            >
              Last saved: {new Date(lastSavedAt).toLocaleString("en-IN")}
            </p>
          )}
        </div>
        <div className="flex gap-3">
          <button
            onClick={preview}
            data-testid="template-preview-button"
            className="inline-flex items-center gap-2 font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#00E5FF] shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
          >
            <FileText size={16} strokeWidth={2.5} /> Preview PDF
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="template-save-button"
            className="inline-flex items-center gap-2 font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-60"
          >
            <Save size={16} strokeWidth={2.5} />
            {saving ? "Saving..." : "Save template"}
          </button>
        </div>
      </div>

      <div className="brutal-card p-6 bg-white">
        {!tpl ? (
          <div className="text-center py-8 font-medium text-[#4A4A4A]">Loading template...</div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map(([key, label]) => (
            <label key={key} className="block">
              <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">
                {label}
              </span>
              {["address_line_2", "footer_note", "terms_line", "header_note"].includes(key) ? (
                <textarea
                  data-testid={`template-${key}`}
                  value={tpl[key] || ""}
                  onChange={(e) => setTpl({ ...tpl, [key]: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
                />
              ) : (
                <input
                  data-testid={`template-${key}`}
                  value={tpl[key] || ""}
                  onChange={(e) => setTpl({ ...tpl, [key]: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
                />
              )}
            </label>
          ))}
        </div>
        )}
      </div>
    </div>
  );
}
